// @ts-check
import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keeps keyboard focus inside a container while active, and calls onEscape when Escape is pressed.
 *
 * Tab and Shift+Tab wrap at the ends, and focus that lands outside - a click on the page behind - is
 * brought back, so the overlay behaves as a modal for keyboard and screen reader users.
 *
 * @param {import('react').RefObject<HTMLElement>} containerRef
 * @param {boolean} active
 * @param {() => void} onEscape
 */
export default function useFocusTrap(containerRef, active, onEscape) {
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const handleKeyDown = (/** @type {KeyboardEvent} */ event) => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(container.querySelectorAll(FOCUSABLE));

      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = /** @type {HTMLElement} */ (focusable[0]);
      const last = /** @type {HTMLElement} */ (focusable[focusable.length - 1]);
      const current = document.activeElement;

      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !container.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (/** @type {FocusEvent} */ event) => {
      const container = containerRef.current;

      if (container && !container.contains(/** @type {Node} */ (event.target))) {
        container.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [containerRef, active, onEscape]);
}
